import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { authService } from '@/services/authService';
import { AuthContextType, User, LoginCredentials, RegisterData } from '@/types/auth';
import { showSuccess } from '@/utils/alerts';

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
}

type AuthAction =
  | { type: 'LOADING' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGOUT' }
  | { type: 'SET_USER'; payload: User };

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('token'),
  isLoading: true,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isLoading: false,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isLoading: false,
      };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          authService.setToken(token);
          const profile = await authService.getProfile();
          dispatch({ type: 'SET_USER', payload: profile });
        } catch (error) {
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          dispatch({ type: 'LOGOUT' });
        }
      } else {
        dispatch({ type: 'LOGOUT' });
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      console.log('🔐 AuthContext: Starting login process for:', credentials.email);
      dispatch({ type: 'LOADING' });
      const response = await authService.login(credentials);
      
      console.log('💾 AuthContext: Storing tokens in localStorage');
      localStorage.setItem('token', response.token);
      localStorage.setItem('refresh_token', response.refresh_token);
      authService.setToken(response.token);
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: response.user, token: response.token },
      });
      
      console.log('✅ AuthContext: Login successful, user authenticated:', response.user.email);
      showSuccess('¡Bienvenido de vuelta!');
    } catch (error: any) {
      console.log('❌ AuthContext: Login failed:', error.response?.data || error.message);
      dispatch({ type: 'LOGOUT' });
      throw error;
    }
  };

  const register = async (data: RegisterData) => {
    try {
      dispatch({ type: 'LOADING' });
      const response = await authService.register(data);
      
      localStorage.setItem('token', response.token);
      localStorage.setItem('refresh_token', response.refresh_token);
      authService.setToken(response.token);
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: response.user, token: response.token },
      });
      
      showSuccess('¡Cuenta creada exitosamente!');
    } catch (error: any) {
      dispatch({ type: 'LOGOUT' });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      // Continue with logout even if API call fails
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      authService.removeToken();
      dispatch({ type: 'LOGOUT' });
      showSuccess('Sesión cerrada correctamente');
    }
  };

  const hasPermission = (permission: string | string[]): boolean => {
    if (!state.user?.role?.permissions) return false;
    
    const userPermissions = state.user.role.permissions;
    
    // Superuser has all permissions
    if (userPermissions.includes('*') || userPermissions.includes('manage_companies')) {
      return true;
    }
    
    if (Array.isArray(permission)) {
      return permission.some(p => userPermissions.includes(p));
    }
    
    return userPermissions.includes(permission);
  };

  const value: AuthContextType = {
    user: state.user,
    token: state.token,
    login,
    register,
    logout,
    isAuthenticated: !!state.user,
    isLoading: state.isLoading,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};